// src/pages/Admin/Dashboard.jsx
import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Button,
  Avatar,
  AvatarGroup,
  Tooltip,
} from '@mui/material';
import {
  ShoppingCart,
  AttachMoney,
  LocalShipping,
  VerifiedUser,
  People,
  TrendingUp,
  TrendingDown,
  MoreVert,
  Inventory,
  Payments,
  HourglassEmpty,
  Warning,
  CheckCircle,
  Refresh,
  Download,
  Notifications,
  ArrowForward,
} from '@mui/icons-material';
import { adminAPI } from '../../services/api';
import { formatCurrency, formatDate, getStatusColor } from '../../utils/helpers';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';

const AdminDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('month');

  useEffect(() => {
    fetchDashboardData();
  }, [timeRange]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getDashboard({ timeRange });
      setDashboardData(response.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchDashboardData();
  };

  const handleExport = async () => {
    try {
      const response = await adminAPI.exportData('dashboard', { timeRange });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `dashboard-report-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error exporting data:', error);
    }
  };

  if (loading) {
    return (
      <Container sx={{ py: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <Box sx={{ textAlign: 'center' }}>
          <LinearProgress sx={{ width: 300, mb: 2 }} />
          <Typography>Loading dashboard data...</Typography>
        </Box>
      </Container>
    );
  }

  const stats = [
    {
      title: 'Total Revenue',
      value: formatCurrency(dashboardData?.totalRevenue || 0),
      icon: <AttachMoney />,
      color: '#4caf50',
      change: dashboardData?.revenueChange || 0,
      trend: dashboardData?.revenueTrend || 'up',
    },
    {
      title: 'Total Orders',
      value: dashboardData?.totalOrders || 0,
      icon: <ShoppingCart />,
      color: '#2196f3',
      change: dashboardData?.orderChange || 0,
      trend: dashboardData?.orderTrend || 'up',
    },
    {
      title: 'Active Shipments',
      value: dashboardData?.activeShipments || 0,
      icon: <LocalShipping />,
      color: '#ff9800',
      change: dashboardData?.shipmentChange || 0,
      trend: dashboardData?.shipmentTrend || 'up',
    },
    {
      title: 'Pending Actions',
      value: dashboardData?.pendingActions || 0,
      icon: <HourglassEmpty />,
      color: '#f44336',
      subItems: [
        { label: 'Verifications', value: dashboardData?.pendingVerifications || 0 },
        { label: 'Payments', value: dashboardData?.pendingPayments || 0 },
        { label: 'Enquiries', value: dashboardData?.pendingEnquiries || 0 },
      ],
    },
    {
      title: 'Total Users',
      value: dashboardData?.totalUsers || 0,
      icon: <People />,
      color: '#9c27b0',
      change: dashboardData?.userChange || 0,
      trend: dashboardData?.userTrend || 'up',
    },
  ];

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Admin Dashboard
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Welcome back! Here's what's happening with your business today.
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={handleRefresh}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<Download />}
            onClick={handleExport}
          >
            Export
          </Button>
        </Box>
      </Box>

      {/* Time Range Selector */}
      <Box sx={{ mb: 3 }}>
        <Button
          size="small"
          variant={timeRange === 'week' ? 'contained' : 'outlined'}
          onClick={() => setTimeRange('week')}
          sx={{ mr: 1 }}
        >
          Week
        </Button>
        <Button
          size="small"
          variant={timeRange === 'month' ? 'contained' : 'outlined'}
          onClick={() => setTimeRange('month')}
          sx={{ mr: 1 }}
        >
          Month
        </Button>
        <Button
          size="small"
          variant={timeRange === 'quarter' ? 'contained' : 'outlined'}
          onClick={() => setTimeRange('quarter')}
          sx={{ mr: 1 }}
        >
          Quarter
        </Button>
        <Button
          size="small"
          variant={timeRange === 'year' ? 'contained' : 'outlined'}
          onClick={() => setTimeRange('year')}
        >
          Year
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {stats.map((stat, index) => (
          <Grid item xs={12} sm={6} md={4} lg={2.4} key={index}>
            <Card sx={{ height: '100%', borderRadius: 2, boxShadow: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box>
                    <Typography color="textSecondary" variant="body2" sx={{ mb: 0.5 }}>
                      {stat.title}
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                      {stat.value}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: `${stat.color}15`, color: stat.color }}>
                    {stat.icon}
                  </Avatar>
                </Box>

                {stat.change !== undefined && (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {stat.trend === 'up' ? (
                        <TrendingUp sx={{ color: '#4caf50', fontSize: 20, mr: 0.5 }} />
                      ) : (
                        <TrendingDown sx={{ color: '#f44336', fontSize: 20, mr: 0.5 }} />
                      )}
                      <Typography
                        variant="body2"
                        sx={{
                          color: stat.trend === 'up' ? '#4caf50' : '#f44336',
                          fontWeight: 'medium',
                        }}
                      >
                        {Math.abs(stat.change)}%
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="textSecondary">
                      vs last period
                    </Typography>
                  </Box>
                )}

                {stat.subItems && (
                  <Box sx={{ mt: 2 }}>
                    {stat.subItems.map((item, idx) => (
                      <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="caption" color="textSecondary">
                          {item.label}
                        </Typography>
                        <Typography variant="caption" fontWeight="medium">
                          {item.value}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Charts */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, borderRadius: 2, height: '100%' }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              Revenue Overview
              <Button size="small" endIcon={<ArrowForward />}>
                View Details
              </Button>
            </Typography>
            <Box sx={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={dashboardData?.revenueChart || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <RechartsTooltip 
                    formatter={(value) => [formatCurrency(value), 'Revenue']}
                    labelFormatter={(label) => `Date: ${label}`}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="revenue" 
                    stroke="#4caf50" 
                    strokeWidth={2}
                    dot={{ r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, borderRadius: 2, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Order Status Distribution
            </Typography>
            <Box sx={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={dashboardData?.orderStatusData || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="status" />
                  <YAxis />
                  <RechartsTooltip />
                  <Bar dataKey="count" fill="#2196f3" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Recent Data Tables */}
      <Grid container spacing={3}>
        {/* Recent Enquiries */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6">Recent Enquiries</Typography>
              <Button size="small" endIcon={<ArrowForward />} onClick={() => window.location.href = '/admin/enquiries'}>
                View All
              </Button>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Enquiry ID</TableCell>
                    <TableCell>Buyer</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dashboardData?.recentEnquiries?.map((enquiry) => (
                    <TableRow key={enquiry._id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          #{enquiry._id.slice(-6)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ width: 30, height: 30, fontSize: 14 }}>
                            {enquiry.buyerName?.charAt(0) || 'B'}
                          </Avatar>
                          <Typography variant="body2">
                            {enquiry.buyerName}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {formatCurrency(enquiry.totalAmount)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={enquiry.status}
                          size="small"
                          color={getStatusColor(enquiry.status)}
                          sx={{ fontWeight: 'medium' }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Recent Shipments */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6">Recent Shipments</Typography>
              <Button size="small" endIcon={<ArrowForward />} onClick={() => window.location.href = '/admin/shipments'}>
                View All
              </Button>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Container</TableCell>
                    <TableCell>Vessel</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>ETA</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dashboardData?.recentShipments?.map((shipment) => (
                    <TableRow key={shipment._id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {shipment.containerNumber}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {shipment.vesselName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={shipment.status}
                          size="small"
                          color={getStatusColor(shipment.status)}
                          sx={{ fontWeight: 'medium' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(shipment.expectedArrival)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* System Status */}
      <Paper sx={{ p: 3, mt: 3, borderRadius: 2, bgcolor: 'grey.50' }}>
        <Typography variant="h6" gutterBottom>
          System Status
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={6} md={3}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar sx={{ bgcolor: '#4caf50', width: 40, height: 40 }}>
                <CheckCircle />
              </Avatar>
              <Box>
                <Typography variant="body2" color="textSecondary">
                  API Status
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  Operational
                </Typography>
              </Box>
            </Box>
          </Grid>
          
          <Grid item xs={6} md={3}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar sx={{ bgcolor: '#4caf50', width: 40, height: 40 }}>
                <CheckCircle />
              </Avatar>
              <Box>
                <Typography variant="body2" color="textSecondary">
                  Database
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  Connected
                </Typography>
              </Box>
            </Box>
          </Grid>
          
          <Grid item xs={6} md={3}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar sx={{ bgcolor: '#ff9800', width: 40, height: 40 }}>
                <Warning />
              </Avatar>
              <Box>
                <Typography variant="body2" color="textSecondary">
                  Storage
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  85% Used
                </Typography>
              </Box>
            </Box>
          </Grid>
          
          <Grid item xs={6} md={3}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar sx={{ bgcolor: '#4caf50', width: 40, height: 40 }}>
                <CheckCircle />
              </Avatar>
              <Box>
                <Typography variant="body2" color="textSecondary">
                  Uptime
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  99.9%
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
};

export default AdminDashboard;